import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

interface PavoPaySettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface PavoPaySettings {
  posIp: string;
  port: string;
  serialNumber: string;
  fingerprint: string;
  sequence: number;
  setupCompleted?: boolean;
  setupDate?: string;
}

const PavoPaySettingsModal: React.FC<PavoPaySettingsModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<PavoPaySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');
  const [error, setError] = useState<string | null>(null);

  // Ayarları yükle
  const loadSettings = async () => {
    console.log('[PavoPay Modal] 🔍 Ayarlar yükleniyor...');
    setLoading(true);
    setError(null);
    
    try {
      // localStorage'daki tüm key'leri kontrol et (debug için)
      console.log('[PavoPay Modal] 📦 LocalStorage keys:', Object.keys(localStorage));
      
      // ✅ YENİ: Local-service API'sinden ayarları oku (Electron dosya sistemi)
      console.log('[PavoPay Modal] 🌐 Local-service API\'sinden ayarlar isteniyor...');
      const response = await fetch('http://localhost:3003/api/yazarkasa/pavopay-get-settings');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('[PavoPay Modal] 📡 API yanıtı:', result);
      
      if (result.success && result.settings) {
        const parsedSettings = result.settings as PavoPaySettings;
        console.log('[PavoPay Modal] ✅ Ayarlar alındı:', parsedSettings);
        setSettings(parsedSettings);
        
        // Ayarlar varsa otomatik test yap
        if (parsedSettings.setupCompleted && parsedSettings.posIp && parsedSettings.serialNumber) {
          console.log('[PavoPay Modal] 🔌 Otomatik bağlantı testi başlatılıyor...');
          testConnection(parsedSettings);
        } else {
          console.warn('[PavoPay Modal] ⚠️ Kurulum tamamlanmamış veya ayarlar eksik');
          setConnectionStatus('unknown');
        }
      } else {
        console.warn('[PavoPay Modal] ⚠️ Ayarlar bulunamadı:', result.message);
        setSettings(null);
        setConnectionStatus('unknown');
        setError(result.message || t('pavoPaySettingsModal.errors.settingsNotFound'));
      }
    } catch (err: any) {
      console.error('[PavoPay Modal] ❌ Ayarlar okunamadı:', err);
      setError(t('pavoPaySettingsModal.errors.settingsLoadFailed', { message: err.message }));
      setSettings(null);
      setConnectionStatus('unknown');
    } finally {
      setLoading(false);
    }
  };

  // Bağlantı testi
  const testConnection = async (settingsToTest?: PavoPaySettings) => {
    const testSettings = settingsToTest || settings;
    
    if (!testSettings) {
      console.error('[PavoPay Modal] ❌ Test edilecek ayarlar yok');
      setError(t('pavoPaySettingsModal.errors.settingsNotFound'));
      return;
    }

    console.log('[PavoPay Modal] 🔌 Bağlantı testi başlatılıyor:', {
      posIp: testSettings.posIp,
      port: testSettings.port,
      serialNumber: testSettings.serialNumber,
      fingerprint: testSettings.fingerprint
    });

    setTesting(true);
    setError(null);

    try {
      const proxyUrl = 'http://localhost:3003/api/yazarkasa/pavopay-pairing';
      console.log('[PavoPay Modal] 📡 Proxy URL:', proxyUrl);
      
      const requestBody = {
        posIp: testSettings.posIp,
        port: testSettings.port,
        serialNumber: testSettings.serialNumber,
        fingerprint: testSettings.fingerprint,
      };
      console.log('[PavoPay Modal] 📤 Request body:', requestBody);
      
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      console.log('[PavoPay Modal] 📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PavoPay Modal] ❌ HTTP error:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[PavoPay Modal] ✅ Test sonucu:', result);

      if (result.success) {
        console.log('[PavoPay Modal] 🟢 Bağlantı başarılı');
        setConnectionStatus('connected');
      } else {
        console.warn('[PavoPay Modal] 🔴 Bağlantı başarısız:', result.message);
        setConnectionStatus('disconnected');
        setError(result.message || t('pavoPaySettingsModal.connection.disconnected'));
      }
    } catch (err: any) {
      console.error('[PavoPay Modal] ❌ Test hatası:', err);
      setConnectionStatus('disconnected');
      setError(t('pavoPaySettingsModal.errors.connectionTestFailed', { message: err.message || t('pavoPaySettingsModal.errors.unknown') }));
    } finally {
      setTesting(false);
    }
  };

  // Modal açıldığında ayarları yükle
  useEffect(() => {
    if (open) {
      console.log('[PavoPay Modal] 🚀 Modal açıldı - ayarlar yükleniyor...');
      loadSettings();
    } else {
      console.log('[PavoPay Modal] 🔒 Modal kapandı');
    }
  }, [open]);

  // Local-service kurulum sayfasını aç
  const openSetupPage = () => {
    console.log('[PavoPay Modal] 🔧 Local-service kurulum sayfası açılıyor...');
    window.open('http://localhost:3003/', '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">{t('pavoPaySettingsModal.title')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !settings ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <InfoIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('pavoPaySettingsModal.setupNotFound.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pavoPaySettingsModal.setupNotFound.description')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={openSetupPage}
            >
              {t('pavoPaySettingsModal.actions.openSetupPage')}
            </Button>
          </Box>
        ) : (
          <>
            {/* Bağlantı Durumu */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: connectionStatus === 'connected' 
                  ? 'success.light' 
                  : connectionStatus === 'disconnected' 
                    ? 'error.light' 
                    : 'grey.100',
                border: 1,
                borderColor: connectionStatus === 'connected' 
                  ? 'success.main' 
                  : connectionStatus === 'disconnected' 
                    ? 'error.main' 
                    : 'grey.300',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {connectionStatus === 'connected' ? (
                    <>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t('pavoPaySettingsModal.connection.connected')}
                      </Typography>
                    </>
                  ) : connectionStatus === 'disconnected' ? (
                    <>
                      <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t('pavoPaySettingsModal.connection.disconnected')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <InfoIcon sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t('pavoPaySettingsModal.connection.unknown')}
                      </Typography>
                    </>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={() => testConnection()}
                  disabled={testing}
                >
                  {testing ? t('pavoPaySettingsModal.actions.testing') : t('pavoPaySettingsModal.actions.testConnection')}
                </Button>
              </Box>
            </Paper>

            {/* Hata Mesajı */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {/* Debug Bilgisi - localStorage ve API kontrolü */}
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {t('pavoPaySettingsModal.settingsSource.title')}
              </Typography>
              <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                {t('pavoPaySettingsModal.settingsSource.lines.api')}{'\n'}
                {t('pavoPaySettingsModal.settingsSource.lines.file')}{'\n'}
                {t('pavoPaySettingsModal.settingsSource.lines.location')}{'\n'}
                {settings ? t('pavoPaySettingsModal.settingsSource.lines.loaded') : t('pavoPaySettingsModal.settingsSource.lines.notFound')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadSettings}
                sx={{ mt: 1 }}
              >
                {t('pavoPaySettingsModal.actions.reload')}
              </Button>
            </Alert>

            {/* Ayarlar Bilgileri */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              {t('pavoPaySettingsModal.setupInfo.title')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('pavoPaySettingsModal.setupInfo.posIp')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {settings.posIp}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Port
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {settings.port}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Protocol
                  </Typography>
                  <Chip 
                    label={settings.port === '4567' ? 'HTTPS' : 'HTTP'} 
                    size="small" 
                    color={settings.port === '4567' ? 'success' : 'primary'}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('pavoPaySettingsModal.setupInfo.serialNumber')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {settings.serialNumber}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('pavoPaySettingsModal.setupInfo.appId')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {settings.fingerprint}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Transaction Sequence
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {settings.sequence}
                  </Typography>
                </Paper>
              </Grid>

              {settings.setupDate && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('pavoPaySettingsModal.setupInfo.setupDate')}
                    </Typography>
                    <Typography variant="body2">
                      {new Date(settings.setupDate).toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Bilgilendirme */}
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                {t('pavoPaySettingsModal.infoNote')}
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>
          {t('pavoPaySettingsModal.actions.close')}
        </Button>
        {settings && (
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={openSetupPage}
          >
            {t('pavoPaySettingsModal.actions.editSettings')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PavoPaySettingsModal;

